import React from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import Footer from './Footer';
import Spinner from './Spinner';
import { recordSocialClick } from '../actions/registerUser';
import PublicProfile from './PublicProfile';
import ListItem from './ListItem';
import PaymentListItem from './PaymentListItem';
import AppTopBar from './AppTopBar';
import {
  DEFAULT_AVATAR,
  DEFAULT_COVER,
  handleAvatarError,
} from '../utils/imageFallback';
import { computeSocialDisplay, sortEntriesForDisplay } from '../utils/socialPlatforms';
import usePaymentAccordion from '../utils/usePaymentAccordion';

const Dashboard = ({
  authh: { isAuth, loading },
  user,
  recordSocialClick,
}) => {
  const { id } = useParams();
  // Above the two early returns below -- the PublicProfile hand-off and
  // the spinner. A hook after a conditional return would run on some
  // renders and not others, which React rejects outright.
  const { isOpen: isPaymentOpen, toggle: togglePayment } = usePaymentAccordion();

  // A user with no userName set yet (fresh registration, or a claimed
  // but not-yet-named pre-provisioned card) still owns their lengthId
  // URL -- match on it too so they land on their own dashboard instead
  // of being treated as a visitor to their own profile. Only while
  // userName is unset, though: once one is set, visiting the lengthId
  // URL must still fall through to PublicProfile so its self-heal
  // redirect (see PublicProfile.js) can canonicalize to the username
  // URL -- that pre-existing behavior, not something this unset-userName
  // case should disable.
  const ownsThisUrl =
    user && (id === user.userName || (!user.userName && id === user.lengthId));
  if (!ownsThisUrl && !loading) {
    return <PublicProfile />;
  }

  const handleClicks = (entry) => {
    recordSocialClick(entry, user._id);
  };

  if (loading || !user.social) return <Spinner />;

  // Same ordering the visitor sees (see sortEntriesForDisplay): the
  // owner's own view of their profile must not disagree with it.
  const orderedEntries = sortEntriesForDisplay(user.socialList);
  const socialDisplay = computeSocialDisplay(orderedEntries);
  const socialItems = orderedEntries.map((entry, index) => ({
    entry,
    ...socialDisplay[index],
  }));

  return (
      <div>
        <div className="my-profile">
          <div className="container" style={{ padding: '0' }}>
            <AppTopBar />
            <div className="row" style={{ margin: 0 }}>
              <div
                className="col-12 cover"
                style={{
                  backgroundImage: user.coverUrl
                    ? `url(${user.coverUrl})`
                    : `url(${DEFAULT_COVER})`,
                }}
              >
                <div className="wrapper">
                  <div className="my-profile-photo">
                    <img
                      src={user.avatarUrl || DEFAULT_AVATAR}
                      alt="photo"
                      id="profileImg"
                      onError={handleAvatarError}
                    />
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  width: '100%',
                }}
              >
                <h1 id="name">{user.name} </h1>

                <p id="bio" style={{ whiteSpace: 'pre-line' }}>
                  {user.bio}
                </p>
              </div>
              <b className="text-center mt-2 mb-2 d-block">
                <div className="col-12 social2">
                  <ul className="row">
                    {socialItems.map(({ entry, name, subtitle }, index) => {
                      const key = entry._id || `${entry.platform}-${index}`;
                      // The owner sees their own profile exactly as a
                      // visitor does, payment rows included -- otherwise
                      // the one person who most needs to check an IBAN
                      // renders correctly never sees it rendered.
                      return entry.kind === 'payment' ? (
                        <PaymentListItem
                          key={key}
                          entry={entry}
                          displayName={name}
                          subtitle={subtitle}
                          handleClicks={handleClicks}
                          isPublic={false}
                          profileKey={user.lengthId}
                          isOpen={isPaymentOpen(key)}
                          onToggle={() => togglePayment(key)}
                          size={'14px'}
                        />
                      ) : (
                        <ListItem
                          key={key}
                          entry={entry}
                          displayName={name}
                          subtitle={subtitle}
                          handleClicks={handleClicks}
                          isPublic={false}
                          size={'14px'}
                        />
                      );
                    })}
                  </ul>
                </div>
              </b>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    );
};

const mapStateToProps = (state) => ({
  authh: state.registerUser,
  user: state.registerUser.user,
});

export default connect(mapStateToProps, { recordSocialClick })(Dashboard);
